/**
 * Special Service → "Supplies & Back Up Meals" — the POS-styled dialog.
 *
 * Same shell as the cafeteria register's `PosSaleDialog`: a wide, full-bleed
 * two-column card — a tile catalog on the left, a summary column on the
 * right — with inline `Alert`s for errors and `Skeleton` tiles while loading.
 *
 * Two tabs:
 *
 *   - **Fresh Backup Meals** — the campus's standing weekly backup order, one
 *     per mealtime with a published BACK_UP menu. Tiles are the menu's live
 *     items; the stepper sets how many of each ship EVERY service day the
 *     weekday chips select. Checkout confirmation replaces that mealtime's standing order
 *     (`PUT /api/school-orders/back-up-meals`); zeroing every tile removes it.
 *     Billing needs nothing here: unclaimed units stay on the school's batch,
 *     units a student takes at the register are drawn to the family.
 *   - **Shelf-Stable Backup Meals** — the date-free bulk checkout under Backup Meals.
 *     The Supplies entry uses the same checkout for non-meal supplies and swag.
 */

import { Alert, AlertDescription } from '@ordo/ui/alert'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { LoadingButton } from '@ordo/ui/loading-button'
import { Skeleton } from '@ordo/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { type BackUpStandingOrderWire, useBackUpStandingOrdersQuery, type Weekday } from '@/api/school-back-up-meals'
import type { AdminSchool } from '@/api/school-orders'
import { QuantityTile } from '@/components/orders/shared/quantity-tile'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import {
  BACK_UP_WEEKDAY_CHIPS,
  type BackUpDraft,
  backUpMealsPerDay,
  backUpMealtimes,
  buildBackUpSavePayload,
  draftFromStandingOrder,
  hasBackUpChanges,
  stepBackUpQuantity,
  toggleBackUpWeekday,
} from '@/lib/school-orders/special-service'
import { serviceCheckoutUrl, useSchoolServiceCart } from '@/stores/school-service-cart'

export type SuppliesBackUpTab = 'backup' | 'supplies'

export interface SuppliesBackUpDialogProps {
  school: AdminSchool
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The Supplies tab body. Rendered only when `school.hasSupplyCatalog`; the
   * supplies work supplies this node (catalog tiles + cart + submit).
   */
  suppliesTab?: ReactNode
  /** Which tab opens first. Defaults to backup when the campus has a BACK_UP menu. */
  initialTab?: SuppliesBackUpTab
  envelope?: SuppliesBackUpTab
}

export function SuppliesBackUpDialog({
  school,
  open,
  onOpenChange,
  suppliesTab,
  initialTab,
  envelope,
}: SuppliesBackUpDialogProps): ReactElement {
  const hasBackUp =
    envelope !== 'supplies' && school.backUpMealsEnabled === true && (school.backUpMealtimes?.length ?? 0) > 0
  const hasSupplies = school.bulkSuppliesEnabled === true && school.hasSupplyCatalog === true
  const [tab, setTab] = useState<SuppliesBackUpTab>(
    initialTab ?? (envelope === 'backup' && hasSupplies ? 'supplies' : hasBackUp ? 'backup' : 'supplies'),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:h-[min(680px,85vh)] sm:max-w-[1012px] sm:rounded-2xl">
        <div className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="px-6 pt-6 pr-14 text-left sm:pl-6">
            <DialogTitle className="text-2xl font-semibold">
              {envelope === 'supplies'
                ? 'Supplies'
                : envelope === 'backup'
                  ? 'Backup Meals'
                  : 'Supplies & Back Up Meals'}
            </DialogTitle>
            <DialogDescription>{school.name}</DialogDescription>
          </DialogHeader>
          {hasBackUp && hasSupplies ? (
            <Tabs
              value={tab}
              onValueChange={(value) => setTab(value as SuppliesBackUpTab)}
              className="flex min-h-0 flex-1 flex-col">
              <TabsList className="mx-6 mt-4 w-fit">
                <TabsTrigger value="backup">Fresh Backup Meals</TabsTrigger>
                <TabsTrigger value="supplies">
                  {envelope === 'backup' ? 'Shelf-Stable Backup Meals' : 'Supplies'}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="backup" className="mt-0 flex min-h-0 flex-1 flex-col">
                {open ? <BackUpMealsTab school={school} /> : null}
              </TabsContent>
              <TabsContent value="supplies" className="mt-0 flex min-h-0 flex-1 flex-col">
                {suppliesTab}
              </TabsContent>
            </Tabs>
          ) : hasBackUp ? (
            open ? (
              <BackUpMealsTab school={school} />
            ) : null
          ) : hasSupplies ? (
            <div className="flex min-h-0 flex-1 flex-col">{suppliesTab}</div>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              {envelope === 'backup' ? 'No backup menu' : 'No supplies catalog'} is currently available for this campus.{' '}
              Contact Ordo Support to set it up.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Fresh Backup Meals tab
// ---------------------------------------------------------------------------

function BackUpMealsTab({ school }: { school: AdminSchool }): ReactElement {
  const query = useBackUpStandingOrdersQuery(school.id)
  const navigate = useNavigate()
  const put = useSchoolServiceCart((state) => state.put)
  const orders = useMemo(() => query.data?.standingOrders ?? [], [query.data])
  const mealtimes = useMemo(() => backUpMealtimes(orders), [orders])
  const [mealtime, setMealtime] = useState<Mealtime | null>(null)
  const activeMealtime = mealtime && mealtimes.includes(mealtime) ? mealtime : (mealtimes[0] ?? null)
  const order = orders.find((o) => o.mealtime === activeMealtime) ?? null

  // One draft per mealtime, seeded from the saved picture the first time a
  // mealtime is shown and re-seeded after a successful save (the query
  // refetches, `order` changes identity, the effect below re-seeds).
  const [drafts, setDrafts] = useState<Partial<Record<Mealtime, BackUpDraft>>>({})
  useEffect(() => {
    if (!order) {
      return
    }
    setDrafts((prev) => (prev[order.mealtime] ? prev : { ...prev, [order.mealtime]: draftFromStandingOrder(order) }))
  }, [order])
  const draft = order ? (drafts[order.mealtime] ?? draftFromStandingOrder(order)) : null
  const setDraft = (next: BackUpDraft): void => {
    if (order) {
      setDrafts((prev) => ({ ...prev, [order.mealtime]: next }))
    }
  }

  const dirty = order && draft ? hasBackUpChanges(order, draft) : false
  const perDay = draft ? backUpMealsPerDay(draft.quantities) : 0

  const handleSave = (): void => {
    if (!order || !draft) {
      return
    }
    const input = buildBackUpSavePayload(school.id, order, draft)
    put({
      id: `standing:${school.id}:${order.mealtime}`,
      kind: 'standing',
      locationId: school.id,
      locationName: school.name,
      title: input.items.length ? 'Fresh backup standing order' : 'Remove fresh backup standing order',
      input,
      lines: input.items.map((line) => ({
        name: order.items.find((item) => item.menuItemId === line.menuItemId)?.name ?? 'Backup meal',
        quantity: line.quantity,
        unitPriceCents: null,
        detail: `${MEALTIME_LABELS[order.mealtime]} · ${draft.weekdays.map((day) => BACK_UP_WEEKDAY_CHIPS.find((chip) => chip.day === day)?.label ?? day).join(', ')} · every week`,
      })),
    })
    navigate(serviceCheckoutUrl(school.id))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto sm:flex-row sm:overflow-visible">
      {/* ── Catalog (left) ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 p-6 sm:min-h-0 sm:flex-1">
        {mealtimes.length > 1 ? (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Mealtime">
            {mealtimes.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={m === activeMealtime}
                onClick={() => setMealtime(m)}
                className={cn(
                  'h-9 rounded-full border px-4 text-sm font-medium transition-colors',
                  m === activeMealtime
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border bg-background text-foreground hover:bg-muted/50',
                )}>
                {MEALTIME_LABELS[m]}
              </button>
            ))}
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Reserve meals delivered every service day you select. Meals a student takes at the register bill the family;
          the rest stay with the school.
        </p>
        <div className="overflow-x-hidden sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
          {query.isError ? (
            <Alert variant="error">
              <AlertDescription>Could not load the backup menu. Close and try again.</AlertDescription>
            </Alert>
          ) : query.isPending ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="aspect-square rounded-2xl" />
              ))}
            </div>
          ) : !order || !draft ? (
            <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
              No published Fresh Backup Meals menu at this campus.
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4">
              {order.items.map((item) => (
                <QuantityTile
                  key={item.menuItemId}
                  name={item.name}
                  qty={draft.quantities[item.menuItemId] ?? 0}
                  max={item.quantityCap}
                  disabled={false}
                  caption={typeof item.quantityCap === 'number' ? `Up to ${item.quantityCap} per day` : 'Per day'}
                  onAdd={() =>
                    setDraft({
                      ...draft,
                      quantities: stepBackUpQuantity(draft.quantities, item.menuItemId, 1, item.quantityCap),
                    })
                  }
                  onDecrement={() =>
                    setDraft({
                      ...draft,
                      quantities: stepBackUpQuantity(draft.quantities, item.menuItemId, -1, item.quantityCap),
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Standing order (right) ─────────────────────────────────── */}
      <div className="flex flex-col border-t sm:w-[400px] sm:shrink-0 sm:border-t-0 sm:border-l">
        <div className="flex flex-1 flex-col gap-5 p-6">
          <h3 className="text-2xl font-semibold">Standing weekly order</h3>
          {order && draft ? (
            <>
              <div>
                <span className="text-sm font-semibold text-muted-foreground">Delivered on</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {BACK_UP_WEEKDAY_CHIPS.map(({ day, label }) => {
                    const on = draft.weekdays.includes(day)
                    return (
                      <button
                        key={day}
                        type="button"
                        aria-pressed={on}
                        disabled={false}
                        onClick={() => setDraft({ ...draft, weekdays: toggleBackUpWeekday(draft.weekdays, day) })}
                        className={cn(
                          'h-8 rounded-full border px-3 text-xs font-medium transition-colors',
                          on
                            ? 'border-transparent bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:bg-muted/50',
                        )}>
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <StandingOrderLines order={order} draft={draft} />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Pick a mealtime to set its standing order.</p>
          )}
        </div>
        <div className="border-t p-6">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-muted-foreground">Meals per day</span>
            <span className="text-2xl font-semibold tabular-nums">{perDay}</span>
          </div>
          <LoadingButton
            type="button"
            className="mt-4 h-11 w-full rounded-full text-base"
            disabled={!dirty || !order}
            onClick={() => void handleSave()}>
            {perDay === 0 && order?.subscription ? 'Review removal' : 'Add to cart'}
          </LoadingButton>
        </div>
      </div>
    </div>
  )
}

function StandingOrderLines({ order, draft }: { order: BackUpStandingOrderWire; draft: BackUpDraft }): ReactElement {
  const lines = order.items.filter((item) => (draft.quantities[item.menuItemId] ?? 0) > 0)
  if (lines.length === 0) {
    return <p className="text-sm text-muted-foreground">Tap a meal to add it to the standing order.</p>
  }
  return (
    <ul className="flex flex-col gap-2">
      {lines.map((item) => (
        <li key={item.menuItemId} className="flex items-baseline justify-between text-sm">
          <span className="min-w-0 truncate">{item.name}</span>
          <span className="tabular-nums">{draft.quantities[item.menuItemId]} / day</span>
        </li>
      ))}
    </ul>
  )
}

/** Exposed for the weekday type re-export convenience of callers/tests. */
export type { Weekday }
